import { Inject, Injectable } from '@angular/core';
import { filter, take } from 'rxjs/operators';
import { IShutdownBroker } from 'src/app/business/maintenance/contracts/shutdown-broker';

import makeDebug from '../../../../makeDebug';
import { ConnectionStateService } from '../../services/connection-state.service';
import { ShutdownBroker } from '../../services/maintenance/shutdown-broker.service';
import { ISyncContext } from '../contracts/sync/sync-context';
import { ISyncElement, SyncElementResult } from '../contracts/sync/sync-element';
import { ShutdownInfoListener as Listener } from '../contracts/sync/sync-endpoints';

const debug = makeDebug('sync:shutdown-info-listener');

/**
 * Waits for the first maintenance information of this run and lets the rest of the pipeline know
 * whether the backend is usable.
 *
 * The maintenance flag itself is recorded in the connection state by the `ShutdownBroker` (the
 * producer of the information); this element only waits for it and then reads `isOnline` back
 * into the run context. `filter` runs before `take(1)`: a first `null` emission used to consume
 * the only slot and leave this promise — and the run — pending until the 15-minute timeout.
 */
@Injectable()
export class ShutdownInfoListener implements ISyncElement {
  public get name(): string {
    return Listener;
  }

  constructor(
    @Inject(ShutdownBroker) private _shutdownBroker: IShutdownBroker,
    private _connectionState: ConnectionStateService
  ) {}

  public async execute(context: ISyncContext): Promise<SyncElementResult> {
    debug('entered execute function', context.params.isOnline);
    const result: SyncElementResult = { contextId: context.id, jobDone: false };
    return new Promise(resolve =>
      this._shutdownBroker.shutdownWatch
        .pipe(
          filter(shutdownInfo => shutdownInfo != null),
          take(1)
        )
        .subscribe(shutdownInfo => {
          // a run that started offline (no session for the backend, see SyncTrigger) stays offline
          context.params.isOnline = context.params.isOnline === true && this._connectionState.isOnline;
          debug('shutdown info received, resolving', shutdownInfo, context.params.isOnline);
          result.shutdownInfo = shutdownInfo;
          result.jobDone = true;
          resolve(result);
        })
    );
  }
}
